import { api } from '../api/setup';
import { 
  User, 
  Permission, 
  ValueChain, 
  Country, 
  GetUsersResponse, 
  ValueChainPermissionsResponse,
  CountryPermissionsResponse,
  DataPermissionsResponse,
  PermissionOverview, 
  GrantValueChainPermissionRequest,
  GrantCountryPermissionRequest,
  GrantDataPermissionRequest,
  BulkGrantPermissionRequest, 
  UserWithPermissionsItem,
  UsersWithPermissionsListResponse,
} from '../types';

// Additional interfaces specific to admin API
interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  is_verified?: boolean;
}

const adminApi = {
  // User Management
  async getUsers(params: GetUsersParams = {}): Promise<GetUsersResponse> {
    const response = await api.request.request({
      method: 'GET',
      url: '/api/v1/admin/users',
      query: params
    });
    return response as GetUsersResponse;
  },

  async getUserById(userId: number): Promise<User> {
    const response = await api.request.request({
      method: 'GET',
      url: `/api/v1/admin/users/${userId}`
    });
    return response as User;
  },

  async getUsersWithPermissions(params: {
    skip?: number;
    limit?: number;
    search?: string;
    /** When set, filter users by account active status (server-side, pagination-safe). */
    is_active?: boolean;
  } = {}): Promise<UsersWithPermissionsListResponse> {
    const query: Record<string, string | number | boolean> = {
      skip: params.skip ?? 0,
      limit: params.limit ?? 50,
    };
    if (params.search) query.search = params.search;
    if (params.is_active !== undefined) query.is_active = params.is_active;
    const response = await api.request.request({
      method: 'GET',
      url: '/api/v1/admin/users/with-permissions',
      query,
    });
    return response as UsersWithPermissionsListResponse;
  },

  async patchAdminUser(
    userId: number,
    body: {
      first_name?: string | null;
      last_name?: string | null;
      company?: string | null;
      is_active?: boolean;
      is_verified?: boolean;
      role?: 'user' | 'admin' | 'superuser';
    }
  ): Promise<UserWithPermissionsItem> {
    const response = await api.request.request({
      method: 'PATCH',
      url: `/api/v1/admin/users/${userId}`,
      body,
    });
    return response as UserWithPermissionsItem;
  },

  // Permission Overview
  async getPermissionOverview(): Promise<PermissionOverview> {
    const response = await api.request.request({
      method: 'GET',
      url: '/api/v1/admin/permissions/overview'
    });
    return response as PermissionOverview;
  },

  // Value Chain Permissions
  async getValueChainPermissions(params: { page?: number; limit?: number; user_id?: number } = {}): Promise<ValueChainPermissionsResponse> {
    const response = await api.request.request({
      method: 'GET',
      url: '/api/v1/admin/permissions/value-chains',
      query: params
    });
    return response as ValueChainPermissionsResponse;
  },

  async grantValueChainPermission(request: GrantValueChainPermissionRequest): Promise<Permission> {
    const response = await api.request.request({
      method: 'POST',
      url: '/api/v1/admin/permissions/value-chains/grant',
      body: request
    });
    return response as Permission;
  },

  async bulkGrantValueChainPermission(request: BulkGrantPermissionRequest): Promise<{ success: boolean; created: number; errors: string[] }> {
    const response = await api.request.request({
      method: 'POST',
      url: '/api/v1/admin/permissions/value-chains/bulk',
      body: request
    });
    return response as { success: boolean; created: number; errors: string[] };
  },

  async revokeValueChainPermission(permissionId: number): Promise<{ success: boolean }> {
    const response = await api.request.request({
      method: 'DELETE',
      url: `/api/v1/admin/permissions/value-chains/${permissionId}`
    });
    return response as { success: boolean };
  },

  async updateValueChainPermission(permissionId: number, isActive: boolean): Promise<Permission> {
    const response = await api.request.request({
      method: 'PATCH',
      url: `/api/v1/admin/permissions/value-chains/${permissionId}`,
      query: { is_active: isActive }
    });
    return response as Permission;
  },

  // Country Permissions
  async getCountryPermissions(params: { page?: number; limit?: number; user_id?: number } = {}): Promise<CountryPermissionsResponse> {
    const response = await api.request.request({
      method: 'GET',
      url: '/api/v1/admin/permissions/countries',
      query: params
    });
    return response as CountryPermissionsResponse;
  },

  async grantCountryPermission(request: GrantCountryPermissionRequest): Promise<Permission> {
    const response = await api.request.request({
      method: 'POST',
      url: '/api/v1/admin/permissions/countries/grant',
      body: request
    });
    return response as Permission;
  },

  async bulkGrantCountryPermission(request: BulkGrantPermissionRequest): Promise<{ success: boolean; created: number; errors: string[] }> {
    const response = await api.request.request({
      method: 'POST',
      url: '/api/v1/admin/permissions/countries/bulk',
      body: request
    });
    return response as { success: boolean; created: number; errors: string[] };
  },

  async revokeCountryPermission(permissionId: number): Promise<{ success: boolean }> {
    const response = await api.request.request({
      method: 'DELETE',
      url: `/api/v1/admin/permissions/countries/${permissionId}`
    });
    return response as { success: boolean };
  },

  async updateCountryPermission(permissionId: number, isActive: boolean): Promise<Permission> {
    const response = await api.request.request({
      method: 'PATCH',
      url: `/api/v1/admin/permissions/countries/${permissionId}`,
      query: { is_active: isActive }
    });
    return response as Permission;
  },

  // Data Permissions
  async getDataPermissions(params: { page?: number; limit?: number; user_id?: number } = {}): Promise<DataPermissionsResponse> {
    const response = await api.request.request({
      method: 'GET',
      url: '/api/v1/admin/permissions/data',
      query: params
    });
    return response as DataPermissionsResponse;
  },

  async grantDataPermission(request: GrantDataPermissionRequest): Promise<Permission> {
    const response = await api.request.request({
      method: 'POST',
      url: '/api/v1/admin/permissions/data/grant',
      body: request
    });
    return response as Permission;
  },

  async bulkGrantDataPermission(request: BulkGrantPermissionRequest): Promise<{ success: boolean; created: number; errors: string[] }> {
    const response = await api.request.request({
      method: 'POST',
      url: '/api/v1/admin/permissions/data/bulk',
      body: request
    });
    return response as { success: boolean; created: number; errors: string[] };
  },

  async revokeDataPermission(permissionId: number): Promise<{ success: boolean }> {
    const response = await api.request.request({
      method: 'DELETE',
      url: `/api/v1/admin/permissions/data/${permissionId}`
    });
    return response as { success: boolean };
  },

  async updateDataPermission(permissionId: number, isActive: boolean, totpCode?: string): Promise<Permission> {
    const response = await api.request.request({
      method: 'PATCH',
      url: `/api/v1/admin/permissions/data/${permissionId}`,
      query: totpCode ? { is_active: isActive, totp_code: totpCode } : { is_active: isActive }
    });
    return response as Permission;
  },

  // Resource Lists
  async getValueChains(): Promise<ValueChain[]> {
    const response = await api.request.request({
      method: 'GET',
      url: '/api/v1/admin/resources/value-chains'
    });
    return response as ValueChain[];
  },

  // Graph sources (admin-only)
  async getGraphSources(refresh: boolean = false): Promise<Array<{ source_db: string; label: string; count: number }>> {
    const response: any = await api.request.request({
      method: 'GET',
      url: '/api/v1/admin/resources/graph-sources',
      query: refresh ? { refresh: 1 } : undefined
    });
    const arr: any[] = Array.isArray(response?.data) ? response.data : [];
    return arr
      .filter((x) => x && x.source_db)
      .map((x) => ({
        source_db: String(x.source_db),
        count: typeof x.count === 'number' ? x.count : parseInt(String(x.count || 0), 10) || 0,
        label: `${String(x.source_db)} (${typeof x.count === 'number' ? x.count.toLocaleString() : String(x.count || 0)})`
      }));
  },

  async getCountries(): Promise<Country[]> {
    const response = await api.request.request({
      method: 'GET',
      url: '/api/v1/admin/resources/countries'
    });
    return (response as { countries: Country[] }).countries;
  },

  async getDataTypes(): Promise<string[]> {
    const response = await api.request.request({
      method: 'GET',
      url: '/api/v1/admin/resources/data-types'
    });
    return response as string[];
  },

  // User Role Management
  async updateUserRole(userId: number, role: 'user' | 'admin' | 'superuser'): Promise<{
    success: boolean;
    message: string;
    user: {
      id: number;
      email: string;
      first_name?: string;
      last_name?: string;
      role: string;
    };
  }> {
    const response = await api.request.request({
      method: 'PATCH',
      url: `/api/v1/admin/users/${userId}/role`,
      query: { role }
    });
    return response as {
      success: boolean;
      message: string;
      user: {
        id: number;
        email: string;
        first_name?: string;
        last_name?: string;
        role: string;
      };
    };
  },

  // User Permission Management (full payload incl. financial)
  async getUserPermissions(userId: number): Promise<UserWithPermissionsItem> {
    const response = await api.request.request({
      method: 'GET',
      url: `/api/v1/admin/users/${userId}/permissions`
    });
    return response as UserWithPermissionsItem;
  },


  async toggleUserVerification(userId: number): Promise<User> {
    const response = await api.request.request({
      method: 'PATCH',
      url: `/api/v1/admin/users/${userId}/verification`
    });
    return response as User;
  },

  async deleteUser(userId: number): Promise<{ success: boolean }> {
    const response = await api.request.request({
      method: 'DELETE',
      url: `/api/v1/admin/users/${userId}`
    });
    return response as { success: boolean };
  },

  // Financial Data Permissions
  async getFinancialPermissions(params: { page?: number; limit?: number; user_id?: number } = {}): Promise<{
    permissions: Array<{
      id: number;
      user_id: number;
      user_email: string;
      user_name: string;
      country: string;
      source: string;
      permission_level: string;
      is_active: boolean;
      granted_at: string | null;
      expires_at: string | null;
      notes: string | null;
    }>;
    total: number;
    page: number;
    per_page: number;
  }> {
    const response = await api.request.request({
      method: 'GET',
      url: '/api/v1/admin/permissions/financial-data',
      query: params
    });
    return response as any;
  },

  async getFinancialSources(): Promise<{ success: boolean; data: Array<{ country: string; source: string; label: string }> }> {
    const response = await api.request.request({
      method: 'GET',
      url: '/api/v1/admin/resources/financial-sources'
    });
    return response as { success: boolean; data: Array<{ country: string; source: string; label: string }> };
  },

  async grantFinancialDataPermission(params: {
    user_id: number;
    country: string;
    source: string;
    permission_level: string;
    notes?: string;
    expires_at?: string;
  }): Promise<{ success: boolean; message: string; permission: any }> {
    const response = await api.request.request({
      method: 'POST',
      url: '/api/v1/admin/permissions/financial-data/grant',
      query: params
    });
    return response as { success: boolean; message: string; permission: any };
  },

  async getUserFinancialDataPermissions(userId: number): Promise<{
    success: boolean;
    user: { id: number; email: string; first_name: string; last_name: string };
    permissions: Array<{
      id: number;
      country: string;
      source: string;
      permission_level: string;
      is_active: boolean;
      granted_at: string;
      expires_at: string | null;
      notes: string | null;
    }>;
  }> {
    const response = await api.request.request({
      method: 'GET',
      url: `/api/v1/admin/permissions/financial-data/user/${userId}`
    });
    return response as {
      success: boolean;
      user: { id: number; email: string; first_name: string; last_name: string };
      permissions: Array<{
        id: number;
        country: string;
        source: string;
        permission_level: string;
        is_active: boolean;
        granted_at: string;
        expires_at: string | null;
        notes: string | null;
      }>;
    };
  },

  async revokeFinancialDataPermission(permissionId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.request.request({
      method: 'DELETE',
      url: `/api/v1/admin/permissions/financial-data/${permissionId}`
    });
    return response as { success: boolean; message: string };
  }
};

export default adminApi;
